import { useState, useMemo } from 'react';
import type {
  OwnBanner,
  OwnSponsor,
  OwnEvent,
  OwnContest,
  OwnProgramSponsorship,
} from '@/mocks/ownAds';

type DealType = 'banner' | 'patrocinador' | 'evento' | 'concurso' | 'programa';

interface Props {
  open: boolean;
  onClose: () => void;
  onCreate: (
    type: DealType,
    item: OwnBanner | OwnSponsor | OwnEvent | OwnContest | OwnProgramSponsorship
  ) => void;
}

export default function AdminOwnAdFormModal({ open, onClose, onCreate }: Props) {
  const [type, setType] = useState<DealType>('banner');
  const [errors, setErrors] = useState<Record<string, string>>();
  const [submitting, setSubmitting] = useState(false);

  /* ── Shared state helpers ── */
  const [name, setName] = useState('');
  const [client, setClient] = useState('');
  const [price, setPrice] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [location, setLocation] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');
  const [contactName, setContactName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [date, setDate] = useState('');
  const [attendees, setAttendees] = useState('');
  const [prize, setPrize] = useState('');
  const [programName, setProgramName] = useState('');
  const [slotType, setSlotType] = useState('');
  const [frequency, setFrequency] = useState('');
  const [size, setSize] = useState('');

  const today = useMemo(() => new Date().toISOString().split('T')[0], []);

  const resetAll = () => {
    setType('banner');
    setName('');
    setClient('');
    setPrice('');
    setStartDate('');
    setEndDate('');
    setLocation('');
    setDescription('');
    setCategory('');
    setContactName('');
    setContactEmail('');
    setDate('');
    setAttendees('');
    setPrize('');
    setProgramName('');
    setSlotType('');
    setFrequency('');
    setSize('');
    setErrors({});
  };

  const validate = (): boolean => {
    const e: Record<string, string> = {};
    if (!name.trim()) e.name = 'El nombre es obligatorio.';
    if (!client.trim()) e.client = 'El cliente/sponsor es obligatorio.';
    if (!price.trim() || Number(price) <= 0) e.price = 'El monto debe ser mayor a 0.';

    if (type === 'banner' || type === 'concurso' || type === 'programa') {
      if (!startDate) e.startDate = 'Fecha inicio obligatoria.';
      if (!endDate) e.endDate = 'Fecha fin obligatoria.';
      if (startDate && endDate && new Date(startDate) >= new Date(endDate)) {
        e.endDate = 'La fecha fin debe ser posterior.';
      }
    }
    if (type === 'banner' && !size.trim()) e.size = 'El formato es obligatorio.';
    if (type === 'patrocinador') {
      if (!category.trim()) e.category = 'La categoría es obligatoria.';
      if (!description.trim()) e.description = 'La descripción es obligatoria.';
      if (!contactEmail.trim()) e.contactEmail = 'El email de contacto es obligatorio.';
    }
    if (type === 'evento') {
      if (!date) e.date = 'La fecha del evento es obligatoria.';
      if (!location.trim()) e.location = 'El lugar es obligatorio.';
      if (!attendees.trim() || Number(attendees) < 0) e.attendees = 'Asistentes inválido.';
    }
    if (type === 'concurso' && !prize.trim()) e.prize = 'El premio es obligatorio.';
    if (type === 'programa') {
      if (!programName.trim()) e.programName = 'El programa es obligatorio.';
      if (!slotType.trim()) e.slotType = 'El tipo de espacio es obligatorio.';
      if (!frequency.trim()) e.frequency = 'La frecuencia es obligatoria.';
    }

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;
    setSubmitting(true);

    const id = Date.now();
    const baseAmount = Number(price);

    setTimeout(() => {
      switch (type) {
        case 'banner': {
          const months = Math.max(1, Math.ceil((new Date(endDate).getTime() - new Date(startDate).getTime()) / (1000 * 60 * 60 * 24 * 30)));
          const item: OwnBanner = {
            id,
            name: name.trim(),
            client: client.trim(),
            location: location.trim() || 'Home',
            size: size.trim(),
            price: baseAmount,
            startDate,
            endDate,
            status: 'activo',
            impressions: 0,
            clicks: 0,
            ctr: 0,
            revenue: baseAmount * months,
          };
          onCreate('banner', item);
          break;
        }
        case 'patrocinador': {
          const item: OwnSponsor = {
            id,
            name: name.trim(),
            category: category.trim(),
            description: description.trim(),
            amount: baseAmount,
            since: today,
            logo: name.trim().split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase(),
            status: 'activo',
            contactName: contactName.trim() || client.trim(),
            contactEmail: contactEmail.trim(),
          };
          onCreate('patrocinador', item);
          break;
        }
        case 'evento': {
          const item: OwnEvent = {
            id,
            name: name.trim(),
            sponsor: client.trim(),
            date,
            location: location.trim(),
            amount: baseAmount,
            status: 'confirmado',
            attendees: Number(attendees) || 0,
            revenue: baseAmount,
          };
          onCreate('evento', item);
          break;
        }
        case 'concurso': {
          const item: OwnContest = {
            id,
            name: name.trim(),
            sponsor: client.trim(),
            prize: prize.trim(),
            startDate,
            endDate,
            amount: baseAmount,
            status: 'activo',
            participants: 0,
            revenue: baseAmount,
          };
          onCreate('concurso', item);
          break;
        }
        case 'programa': {
          const months = Math.max(1, Math.ceil((new Date(endDate).getTime() - new Date(startDate).getTime()) / (1000 * 60 * 60 * 24 * 30)));
          const item: OwnProgramSponsorship = {
            id,
            programName: programName.trim(),
            sponsor: client.trim(),
            slotType: slotType.trim(),
            frequency: frequency.trim(),
            price: baseAmount,
            startDate,
            endDate,
            status: 'activo',
            revenue: baseAmount * months,
          };
          onCreate('programa', item);
          break;
        }
      }
      setSubmitting(false);
      resetAll();
      onClose();
    }, 600);
  };

  if (!open) return null;

  const typeLabel: Record<DealType, string> = {
    banner: 'Banner Publicitario',
    patrocinador: 'Patrocinador',
    evento: 'Evento Patrocinado',
    concurso: 'Concurso',
    programa: 'Programa con Sponsor',
  };

  const clearError = (key: string) => setErrors(p => { const n = { ...p }; delete n[key]; return n; });

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose}></div>
      <div className="relative bg-background-50 rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto shadow-lg">
        <div className="sticky top-0 bg-background-50 border-b border-background-200 p-5 flex items-start justify-between gap-4 z-10">
          <div>
            <h3 className="text-foreground-950 font-bold text-sm">Nuevo Acuerdo de Publicidad Propia</h3>
            <p className="text-foreground-400 text-[10px] mt-0.5">Creá un nuevo acuerdo. Todo el revenue es 100% para tu radio.</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center shrink-0 cursor-pointer hover:bg-background-200 transition-colors">
            <i className="ri-close-line text-foreground-500 text-sm"></i>
          </button>
        </div>

        <div className="p-5 space-y-4">
          {/* Type selector */}
          <div>
            <label className="text-foreground-500 text-[10px] font-semibold uppercase block mb-1.5">Tipo de acuerdo *</label>
            <div className="flex items-center gap-1.5 flex-wrap">
              {(Object.keys(typeLabel) as DealType[]).map((t) => (
                <button
                  key={t}
                  onClick={() => { setType(t); setErrors({}); }}
                  className={`px-3 py-1.5 rounded-lg text-[10px] font-semibold transition-all cursor-pointer whitespace-nowrap ${
                    type === t
                      ? 'bg-emerald-500 text-white'
                      : 'bg-background-100 text-foreground-500 hover:bg-background-200'
                  }`}
                >
                  {typeLabel[t]}
                </button>
              ))}
            </div>
          </div>

          {/* Common fields */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-foreground-500 text-[10px] font-semibold uppercase block mb-1.5">
                {type === 'evento' ? 'Nombre del evento' : type === 'programa' ? 'Nombre del acuerdo' : 'Nombre'} *
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => { setName(e.target.value); clearError('name'); }}
                placeholder={type === 'banner' ? 'Banner Home Superior' : type === 'evento' ? 'Fiesta Aniversario' : 'Nombre'}
                className={`w-full px-3 py-2.5 bg-background-50 border rounded-xl text-xs text-foreground-800 placeholder:text-foreground-300 outline-none focus:border-foreground-300 transition-colors ${errors.name ? 'border-red-400' : 'border-background-200'}`}
              />
              {errors.name && <p className="text-red-500 text-[9px] mt-1">{errors.name}</p>}
            </div>
            <div>
              <label className="text-foreground-500 text-[10px] font-semibold uppercase block mb-1.5">
                {type === 'patrocinador' || type === 'evento' || type === 'concurso' || type === 'programa' ? 'Sponsor' : 'Cliente'} *
              </label>
              <input
                type="text"
                value={client}
                onChange={(e) => { setClient(e.target.value); clearError('client'); }}
                placeholder="Marca o empresa"
                className={`w-full px-3 py-2.5 bg-background-50 border rounded-xl text-xs text-foreground-800 placeholder:text-foreground-300 outline-none focus:border-foreground-300 transition-colors ${errors.client ? 'border-red-400' : 'border-background-200'}`}
              />
              {errors.client && <p className="text-red-500 text-[9px] mt-1">{errors.client}</p>}
            </div>
          </div>

          {/* Banner extra */}
          {type === 'banner' && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-foreground-500 text-[10px] font-semibold uppercase block mb-1.5">Ubicación</label>
                <input
                  type="text"
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  placeholder="Home - Top"
                  className="w-full px-3 py-2.5 bg-background-50 border border-background-200 rounded-xl text-xs text-foreground-800 placeholder:text-foreground-300 outline-none focus:border-foreground-300 transition-colors"
                />
              </div>
              <div>
                <label className="text-foreground-500 text-[10px] font-semibold uppercase block mb-1.5">Formato *</label>
                <select
                  value={size}
                  onChange={(e) => { setSize(e.target.value); clearError('size'); }}
                  className={`w-full px-3 py-2.5 bg-background-50 border rounded-xl text-xs text-foreground-800 outline-none focus:border-foreground-300 transition-colors cursor-pointer ${errors.size ? 'border-red-400' : 'border-background-200'}`}
                >
                  <option value="">Seleccionar...</option>
                  <option value="728×90">728×90 Leaderboard</option>
                  <option value="300×250">300×250 Medium</option>
                  <option value="970×90">970×90 Billboard</option>
                  <option value="468×60">468×60 Full Banner</option>
                  <option value="320×50">320×50 Mobile</option>
                </select>
                {errors.size && <p className="text-red-500 text-[9px] mt-1">{errors.size}</p>}
              </div>
            </div>
          )}

          {/* Patrocinador extra */}
          {type === 'patrocinador' && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-foreground-500 text-[10px] font-semibold uppercase block mb-1.5">Categoría *</label>
                  <input
                    type="text"
                    value={category}
                    onChange={(e) => { setCategory(e.target.value); clearError('category'); }}
                    placeholder="Bebidas, Automotor..."
                    className={`w-full px-3 py-2.5 bg-background-50 border rounded-xl text-xs text-foreground-800 placeholder:text-foreground-300 outline-none focus:border-foreground-300 transition-colors ${errors.category ? 'border-red-400' : 'border-background-200'}`}
                  />
                  {errors.category && <p className="text-red-500 text-[9px] mt-1">{errors.category}</p>}
                </div>
                <div>
                  <label className="text-foreground-500 text-[10px] font-semibold uppercase block mb-1.5">Email contacto *</label>
                  <input
                    type="email"
                    value={contactEmail}
                    onChange={(e) => { setContactEmail(e.target.value); clearError('contactEmail'); }}
                    placeholder="contacto@marca.com"
                    className={`w-full px-3 py-2.5 bg-background-50 border rounded-xl text-xs text-foreground-800 placeholder:text-foreground-300 outline-none focus:border-foreground-300 transition-colors ${errors.contactEmail ? 'border-red-400' : 'border-background-200'}`}
                  />
                  {errors.contactEmail && <p className="text-red-500 text-[9px] mt-1">{errors.contactEmail}</p>}
                </div>
              </div>
              <div>
                <label className="text-foreground-500 text-[10px] font-semibold uppercase block mb-1.5">Descripción *</label>
                <textarea
                  value={description}
                  onChange={(e) => { setDescription(e.target.value); clearError('description'); }}
                  rows={3}
                  placeholder="Descripción del patrocinio, menciones, beneficios..."
                  maxLength={500}
                  className={`w-full px-3 py-2.5 bg-background-50 border rounded-xl text-xs text-foreground-800 placeholder:text-foreground-300 outline-none focus:border-foreground-300 transition-colors resize-none ${errors.description ? 'border-red-400' : 'border-background-200'}`}
                />
                <div className="flex justify-between mt-0.5">
                  {errors.description && <p className="text-red-500 text-[9px]">{errors.description}</p>}
                  <p className="text-foreground-300 text-[8px] ml-auto">{description.length}/500</p>
                </div>
              </div>
              <div>
                <label className="text-foreground-500 text-[10px] font-semibold uppercase block mb-1.5">Nombre del contacto</label>
                <input
                  type="text"
                  value={contactName}
                  onChange={(e) => setContactName(e.target.value)}
                  placeholder="Nombre completo"
                  className="w-full px-3 py-2.5 bg-background-50 border border-background-200 rounded-xl text-xs text-foreground-800 placeholder:text-foreground-300 outline-none focus:border-foreground-300 transition-colors"
                />
              </div>
            </>
          )}

          {/* Evento extra */}
          {type === 'evento' && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-foreground-500 text-[10px] font-semibold uppercase block mb-1.5">Fecha del evento *</label>
                  <input
                    type="date"
                    value={date}
                    min={today}
                    onChange={(e) => { setDate(e.target.value); clearError('date'); }}
                    className={`w-full px-3 py-2.5 bg-background-50 border rounded-xl text-xs text-foreground-800 outline-none focus:border-foreground-300 transition-colors ${errors.date ? 'border-red-400' : 'border-background-200'}`}
                  />
                  {errors.date && <p className="text-red-500 text-[9px] mt-1">{errors.date}</p>}
                </div>
                <div>
                  <label className="text-foreground-500 text-[10px] font-semibold uppercase block mb-1.5">Asistentes estimados</label>
                  <input
                    type="number"
                    min={0}
                    value={attendees}
                    onChange={(e) => { setAttendees(e.target.value); clearError('attendees'); }}
                    placeholder="500"
                    className={`w-full px-3 py-2.5 bg-background-50 border rounded-xl text-xs text-foreground-800 placeholder:text-foreground-300 outline-none focus:border-foreground-300 transition-colors ${errors.attendees ? 'border-red-400' : 'border-background-200'}`}
                  />
                  {errors.attendees && <p className="text-red-500 text-[9px] mt-1">{errors.attendees}</p>}
                </div>
              </div>
              <div>
                <label className="text-foreground-500 text-[10px] font-semibold uppercase block mb-1.5">Lugar *</label>
                <input
                  type="text"
                  value={location}
                  onChange={(e) => { setLocation(e.target.value); clearError('location'); }}
                  placeholder="Anfiteatro Municipal"
                  className={`w-full px-3 py-2.5 bg-background-50 border rounded-xl text-xs text-foreground-800 placeholder:text-foreground-300 outline-none focus:border-foreground-300 transition-colors ${errors.location ? 'border-red-400' : 'border-background-200'}`}
                />
                {errors.location && <p className="text-red-500 text-[9px] mt-1">{errors.location}</p>}
              </div>
            </>
          )}

          {/* Concurso extra */}
          {type === 'concurso' && (
            <div>
              <label className="text-foreground-500 text-[10px] font-semibold uppercase block mb-1.5">Premio *</label>
              <input
                type="text"
                value={prize}
                onChange={(e) => { setPrize(e.target.value); clearError('prize'); }}
                placeholder="Viaje a Bariloche, Cena para dos..."
                className={`w-full px-3 py-2.5 bg-background-50 border rounded-xl text-xs text-foreground-800 placeholder:text-foreground-300 outline-none focus:border-foreground-300 transition-colors ${errors.prize ? 'border-red-400' : 'border-background-200'}`}
              />
              {errors.prize && <p className="text-red-500 text-[9px] mt-1">{errors.prize}</p>}
            </div>
          )}

          {/* Programa extra */}
          {type === 'programa' && (
            <>
              <div>
                <label className="text-foreground-500 text-[10px] font-semibold uppercase block mb-1.5">Programa *</label>
                <input
                  type="text"
                  value={programName}
                  onChange={(e) => { setProgramName(e.target.value); clearError('programName'); }}
                  placeholder="Mañanas Express"
                  className={`w-full px-3 py-2.5 bg-background-50 border rounded-xl text-xs text-foreground-800 placeholder:text-foreground-300 outline-none focus:border-foreground-300 transition-colors ${errors.programName ? 'border-red-400' : 'border-background-200'}`}
                />
                {errors.programName && <p className="text-red-500 text-[9px] mt-1">{errors.programName}</p>}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-foreground-500 text-[10px] font-semibold uppercase block mb-1.5">Tipo de espacio *</label>
                  <input
                    type="text"
                    value={slotType}
                    onChange={(e) => { setSlotType(e.target.value); clearError('slotType'); }}
                    placeholder="Menciones en vivo"
                    className={`w-full px-3 py-2.5 bg-background-50 border rounded-xl text-xs text-foreground-800 placeholder:text-foreground-300 outline-none focus:border-foreground-300 transition-colors ${errors.slotType ? 'border-red-400' : 'border-background-200'}`}
                  />
                  {errors.slotType && <p className="text-red-500 text-[9px] mt-1">{errors.slotType}</p>}
                </div>
                <div>
                  <label className="text-foreground-500 text-[10px] font-semibold uppercase block mb-1.5">Frecuencia *</label>
                  <input
                    type="text"
                    value={frequency}
                    onChange={(e) => { setFrequency(e.target.value); clearError('frequency'); }}
                    placeholder="3 por hora"
                    className={`w-full px-3 py-2.5 bg-background-50 border rounded-xl text-xs text-foreground-800 placeholder:text-foreground-300 outline-none focus:border-foreground-300 transition-colors ${errors.frequency ? 'border-red-400' : 'border-background-200'}`}
                  />
                  {errors.frequency && <p className="text-red-500 text-[9px] mt-1">{errors.frequency}</p>}
                </div>
              </div>
            </>
          )}

          {/* Precio */}
          <div>
            <label className="text-foreground-500 text-[10px] font-semibold uppercase block mb-1.5">
              {type === 'patrocinador' || type === 'evento' ? 'Monto (USD) *' : type === 'programa' || type === 'banner' ? 'Precio/mes (USD) *' : 'Monto/premio (USD) *'}
            </label>
            <input
              type="number"
              min={1}
              value={price}
              onChange={(e) => { setPrice(e.target.value); clearError('price'); }}
              placeholder="500"
              className={`w-full px-3 py-2.5 bg-background-50 border rounded-xl text-xs text-foreground-800 placeholder:text-foreground-300 outline-none focus:border-foreground-300 transition-colors ${errors.price ? 'border-red-400' : 'border-background-200'}`}
            />
            {errors.price && <p className="text-red-500 text-[9px] mt-1">{errors.price}</p>}
          </div>

          {/* Fechas para banner / concurso / programa */}
          {(type === 'banner' || type === 'concurso' || type === 'programa') && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-foreground-500 text-[10px] font-semibold uppercase block mb-1.5">Fecha inicio *</label>
                <input
                  type="date"
                  value={startDate}
                  min={today}
                  onChange={(e) => { setStartDate(e.target.value); clearError('startDate'); clearError('endDate'); }}
                  className={`w-full px-3 py-2.5 bg-background-50 border rounded-xl text-xs text-foreground-800 outline-none focus:border-foreground-300 transition-colors ${errors.startDate ? 'border-red-400' : 'border-background-200'}`}
                />
                {errors.startDate && <p className="text-red-500 text-[9px] mt-1">{errors.startDate}</p>}
              </div>
              <div>
                <label className="text-foreground-500 text-[10px] font-semibold uppercase block mb-1.5">Fecha fin *</label>
                <input
                  type="date"
                  value={endDate}
                  min={startDate || today}
                  onChange={(e) => { setEndDate(e.target.value); clearError('endDate'); }}
                  className={`w-full px-3 py-2.5 bg-background-50 border rounded-xl text-xs text-foreground-800 outline-none focus:border-foreground-300 transition-colors ${errors.endDate ? 'border-red-400' : 'border-background-200'}`}
                />
                {errors.endDate && <p className="text-red-500 text-[9px] mt-1">{errors.endDate}</p>}
              </div>
            </div>
          )}
        </div>

        <div className="sticky bottom-0 bg-background-50 border-t border-background-200 p-4 flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-xl bg-background-100 text-foreground-600 text-xs font-medium cursor-pointer whitespace-nowrap hover:bg-background-200 transition-colors"
          >
            Cancelar
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className="px-4 py-2 rounded-xl bg-emerald-500 text-white text-xs font-bold cursor-pointer hover:bg-emerald-600 whitespace-nowrap transition-colors disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-1.5"
          >
            {submitting && <i className="ri-loader-4-line animate-spin text-sm"></i>}
            Crear {typeLabel[type]}
          </button>
        </div>
      </div>
    </div>
  );
}